/**
 * Lightweight, display-only recognisers for pattern text (mock engine). The editor highlighter has its
 * own anchored copies in editor/language.ts.
 * The engine is the only authority on what a line means.
 */

/** Row label at line start: `Row 3:`, `Rows 3-8 (RS):`, `Rnd 5-8:`, `Rang 3 :`, `Tours 1-12 :`, `R4:` */
export const ROW_LABEL_RE =
  /^\s*((?:rows?|rnds?|rounds?|rangs?|rgs?|tours?|r)\s*\.?\s*\d+(?:\s*(?:-|–|à|to|and|et|&)\s*\d+)?)(?:\s*\([^)]{0,16}\))?\s*:/i;

/** Front-matter line: `# key: value`. */
export const FRONT_MATTER_RE = /^#\s*([A-Za-z_][\w-]*)\s*:\s*(.*)$/;

export interface FrontMatter {
  meta: Record<string, string>;
  /** Number of leading source lines that belong to the front matter. */
  lineCount: number;
}

/** Reads the leading `# key: value` block (blank lines inside the block are allowed). */
export function readFrontMatter(text: string): FrontMatter {
  const meta: Record<string, string> = {};
  const lines = text.split('\n');
  let lineCount = 0;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].replace(/\r$/, '');
    const m = FRONT_MATTER_RE.exec(line);
    if (m) {
      meta[m[1].toLowerCase()] = m[2].trim();
      lineCount = i + 1;
    } else if (line.trim() !== '') {
      break;
    }
  }
  return { meta, lineCount };
}

export function rowLabel(line: string): string | null {
  const m = ROW_LABEL_RE.exec(line);
  return m ? m[1].replace(/\s+/g, ' ').trim() : null;
}

/** A short capitalised line without figures or final punctuation: « Calotte », "Turn the heel" (display only). */
export const HEADING_RE = /^\s*[A-ZÀ-ÖØ-Þ][^\d:;.,!?*()]{1,38}\s*$/;

export function isHeadingLine(line: string): boolean {
  return HEADING_RE.test(line) && !ROW_LABEL_RE.test(line);
}
